// Real API Client for backend communication
// Strip undefined/null/empty-string values so they're never sent as query params
function stripEmpty(params) {
    if (!params)
        return undefined;
    const result = {};
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '')
            result[k] = v;
    }
    return Object.keys(result).length ? result : undefined;
}
// API Configuration
const API_BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
    'http://localhost:3000/api/v1';
// HTTP Client with error handling
class HttpClient {
    constructor(baseURL) {
        this.token = null;
        this.baseURL = baseURL;
    }
    setAuthToken(token) {
        this.token = token;
        console.log('[API Client] Auth token set:', token ? `${token.substring(0, 20)}...` : 'null');
    }
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
            console.log('[API Client] Request with auth:', endpoint);
        }
        else {
            console.warn('[API Client] Request WITHOUT auth:', endpoint);
        }
        const config = {
            ...options,
            headers,
        };
        try {
            const response = await fetch(url, config);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
                console.error(`[API Client] ${response.status} error for ${endpoint}:`, errorMessage);
                throw new Error(errorMessage);
            }
            return await response.json();
        }
        catch (error) {
            console.error(`[API Client] Request failed for ${endpoint}:`, error);
            throw error;
        }
    }
    async get(endpoint, params) {
        const url = params ? `${endpoint}?${new URLSearchParams(params).toString()}` : endpoint;
        return this.request(url, { method: 'GET' });
    }
    async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }
}
// Create HTTP client instance
const httpClient = new HttpClient(API_BASE_URL);
// API Client
export const apiClient = {
    // Set authentication token
    setAuthToken: (token) => {
        httpClient.setAuthToken(token);
    },
    // Auth
    auth: {
        async phoneLogin(phone) {
            return httpClient.post('/auth/phone-login', {
                phone,
            });
        },
    },
    // Restaurants
    restaurants: {
        async getAll(params) {
            const queryParams = {};
            if (params?.search)
                queryParams.search = params.search;
            if (params?.type)
                queryParams.type = params.type;
            if (params?.zone_id)
                queryParams.zone_id = params.zone_id;
            if (params?.package_score !== undefined)
                queryParams.package_score = params.package_score.toString();
            if (params?.is_active !== undefined)
                queryParams.is_active = params.is_active.toString();
            return httpClient.get('/restaurants', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/restaurants/${id}`);
        },
        async create(data) {
            return httpClient.post('/restaurants', data);
        },
        async update(id, data) {
            return httpClient.put(`/restaurants/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/restaurants/${id}`);
        },
        async updatePackageScore(id, package_score) {
            return httpClient.patch(`/restaurants/${id}/package-score`, {
                package_score,
            });
        },
        async updateZone(id, zone_id) {
            return httpClient.patch(`/restaurants/${id}/zone`, { zone_id });
        },
    },
    // Menu Items
    menuItems: {
        async getByRestaurant(restaurantId, params) {
            const queryParams = {
                restaurant_id: restaurantId,
            };
            if (params?.category)
                queryParams.category = params.category;
            if (params?.search)
                queryParams.search = params.search;
            return httpClient.get('/menu-items', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/menu-items/${id}`);
        },
        async getCategories(restaurantId) {
            // Get unique categories for a restaurant by fetching all menu items and extracting categories
            const response = await this.getByRestaurant(restaurantId);
            const categories = Array.from(new Set(response.data?.map((item) => item.category).filter(Boolean) || []));
            return {
                success: true,
                data: categories,
                message: 'Categories retrieved successfully',
            };
        },
        async create(data) {
            return httpClient.post('/menu-items', data);
        },
        async update(id, data) {
            return httpClient.put(`/menu-items/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/menu-items/${id}`);
        },
        // Menu Customisations
        async getAllCustomisations() {
            return httpClient.get('/menu-items/customisations');
        },
        async getMenuCustomisations(menuItemId) {
            return httpClient.get(`/menu-items/${menuItemId}/customisations`);
        },
        async createMenuCustomisation(menuItemId, data) {
            return httpClient.post(`/menu-items/${menuItemId}/customisations`, data);
        },
        async updateMenuCustomisation(id, data) {
            return httpClient.put(`/menu-items/customisations/${id}`, data);
        },
        async deleteMenuCustomisation(id) {
            return httpClient.delete(`/menu-items/customisations/${id}`);
        },
        async createCustomisationItem(customisationId, data) {
            return httpClient.post(`/menu-items/customisations/${customisationId}/items`, data);
        },
        async updateCustomisationItem(id, data) {
            return httpClient.put(`/menu-items/customisation-items/${id}`, data);
        },
        async deleteCustomisationItem(id) {
            return httpClient.delete(`/menu-items/customisation-items/${id}`);
        },
    },
    // Orders
    orders: {
        async getAll(params) {
            const queryParams = {};
            if (params?.page)
                queryParams.page = params.page.toString();
            if (params?.limit)
                queryParams.limit = params.limit.toString();
            if (params?.status)
                queryParams.status = params.status;
            if (params?.restaurant_id)
                queryParams.restaurant_id = params.restaurant_id;
            if (params?.customer_id)
                queryParams.customer_id = params.customer_id;
            if (params?.start_date)
                queryParams.start_date = params.start_date;
            if (params?.end_date)
                queryParams.end_date = params.end_date;
            return httpClient.get('/orders', queryParams);
        },
        async getToday(params) {
            const queryParams = {};
            if (params?.order_status)
                queryParams.order_status = params.order_status;
            if (params?.restaurant_id)
                queryParams.restaurant_id = params.restaurant_id;
            return httpClient.get('/orders/today', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/orders/${id}`);
        },
        async create(data) {
            return httpClient.post('/orders', data);
        },
        async updateStatus(id, status) {
            return httpClient.patch(`/orders/${id}/status`, { order_status: status });
        },
        async updateInternalNotes(id, internal_notes) {
            return httpClient.patch(`/orders/${id}/internal-notes`, { internal_notes });
        },
    },
    // Users
    users: {
        async getAll(params) {
            const queryParams = {};
            if (params?.search)
                queryParams.search = params.search;
            if (params?.role)
                queryParams.role = params.role;
            return httpClient.get('/users', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/users/${id}`);
        },
        async getProfile() {
            return httpClient.get('/users/profile');
        },
        async create(data) {
            return httpClient.post('/users/register', data);
        },
        async update(id, data) {
            return httpClient.put(`/users/profile`, data);
        },
    },
    // Zones
    zones: {
        async getAll(params) {
            const queryParams = {};
            if (params?.search)
                queryParams.search = params.search;
            if (params?.is_active !== undefined)
                queryParams.is_active = params.is_active.toString();
            return httpClient.get('/zones', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/zones/${id}`);
        },
        async create(data) {
            return httpClient.post('/zones', data);
        },
        async update(id, data) {
            return httpClient.put(`/zones/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/zones/${id}`);
        },
        async getRestaurants(id) {
            return httpClient.get(`/zones/${id}/restaurants`);
        },
        async getDeliveryCost(id) {
            return httpClient.get(`/zones/${id}/delivery-cost`);
        },
    },
    // Zone Mappings
    zoneMappings: {
        async getAll(params) {
            const queryParams = {};
            if (params?.from_zone_id)
                queryParams.from_zone_id = params.from_zone_id;
            if (params?.to_zone_id)
                queryParams.to_zone_id = params.to_zone_id;
            if (params?.is_active !== undefined)
                queryParams.is_active = params.is_active.toString();
            return httpClient.get('/zone-mappings', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/zone-mappings/${id}`);
        },
        async create(data) {
            return httpClient.post('/zone-mappings', data);
        },
        async update(id, data) {
            return httpClient.put(`/zone-mappings/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/zone-mappings/${id}`);
        },
        async calculateDeliveryCost(fromZoneId, toZoneId) {
            const queryParams = {
                from_zone_id: fromZoneId,
                to_zone_id: toZoneId,
            };
            return httpClient.get('/zone-mappings/calculate-cost', queryParams);
        },
    },
    // Ads
    ads: {
        async getAll(params) {
            const queryParams = {};
            if (params?.search)
                queryParams.search = params.search;
            if (params?.is_active !== undefined)
                queryParams.is_active = params.is_active.toString();
            if (params?.start_date)
                queryParams.start_date = params.start_date;
            if (params?.end_date)
                queryParams.end_date = params.end_date;
            if (params?.active_on_date)
                queryParams.active_on_date = params.active_on_date;
            return httpClient.get('/ads', queryParams);
        },
        async getActive() {
            return httpClient.get('/ads/active');
        },
        async getById(id) {
            return httpClient.get(`/ads/${id}`);
        },
        async create(data) {
            return httpClient.post('/ads', data);
        },
        async update(id, data) {
            return httpClient.put(`/ads/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/ads/${id}`);
        },
        async toggleActive(id) {
            return httpClient.patch(`/ads/${id}/toggle-active`);
        },
        async schedule(id, data) {
            return httpClient.patch(`/ads/${id}/schedule`, data);
        },
    },
    // Dashboard
    dashboard: {
        async getMetrics() {
            return httpClient.get('/dashboard/metrics');
        },
    },
    // Analytics
    analytics: {
        async getOverview(params) {
            return httpClient.get('/analytics/overview', stripEmpty(params));
        },
        async getRestaurants(params) {
            return httpClient.get('/analytics/restaurants', stripEmpty(params));
        },
        async getCustomers(params) {
            return httpClient.get('/analytics/customers', stripEmpty(params));
        },
        async getCancellations(params) {
            return httpClient.get('/analytics/cancellations', stripEmpty(params));
        },
        async export(params) {
            return httpClient.get('/analytics/export', stripEmpty(params));
        },
        async getDelivery(params) {
            return httpClient.get('/analytics/delivery', stripEmpty(params));
        },
        async getCustomerSales(params) {
            return httpClient.get('/analytics/customer-sales', stripEmpty(params));
        },
    },
    // Staff Users
    staffUsers: {
        async getAll() {
            return httpClient.get('/staff-users');
        },
        async getById(id) {
            return httpClient.get(`/staff-users/${id}`);
        },
        async getBySupabaseId(supabaseUserId) {
            return httpClient.get(`/staff-users/by-supabase-id/${supabaseUserId}`);
        },
        async create(data) {
            return httpClient.post('/staff-users', data);
        },
        async update(id, data) {
            return httpClient.put(`/staff-users/${id}`, data);
        },
        async delete(id) {
            return httpClient.delete(`/staff-users/${id}`);
        },
        async resetPassword(id, password) {
            return httpClient.post(`/staff-users/${id}/reset-password`, { password });
        },
    },
    // Customer Users
    customerUsers: {
        async getAll(params) {
            const queryParams = {};
            if (params?.search)
                queryParams.search = params.search;
            if (params?.page)
                queryParams.page = params.page.toString();
            if (params?.limit)
                queryParams.limit = params.limit.toString();
            return httpClient.get('/customer-users', queryParams);
        },
        async getById(id) {
            return httpClient.get(`/customer-users/${id}`);
        },
        async updateStatus(id, is_active) {
            return httpClient.put(`/customer-users/${id}/status`, { is_active });
        },
    },
    // System Settings
    system: {
        async getSettings() {
            return httpClient.get('/system/settings');
        },
        async setAllRestaurantsClosed(closed) {
            return httpClient.put('/system/settings/all-restaurants-closed', { closed });
        },
    },
};
//# sourceMappingURL=index.js.map